/**
 * Placement inventory for one script, as produced by the O2 placement agent.
 *
 * Internal demo page. At /placements it shows a checked-in snapshot of a real run — no
 * server, no queue, nothing to fail. /placements/live uploads a script, runs the agent for
 * real, and renders the result through this same component via the `data` prop, so the two
 * cannot drift: if a column works on the saved run it works on the live one.
 *
 * The page is built to answer the question that follows every inventory table: WHY is
 * that row worth 15 seconds and not 6? Placement-vs-Narrative IS the price (6s vs 15s,
 * times mentions), so the derivation is shown next to the number rather than hidden.
 */
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Eye,
  FileText,
  MessageSquare,
  ShieldAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import snapshot from "@/data/detoursPlacements";
import type { Placement, PlacementRun } from "@/types/placement-run";

/** Strip the numeric id the catalog prefixes onto every category name. */
const shortCategory = (c: string | null) =>
  (c ?? "").replace(/^\d+:\s*/, "").replace(/ > /g, " › ") || "—";

/** The model writes "product (short context)"; the parenthetical is noise in a table. */
const productName = (t: string | null) => (t ?? "").replace(/\s*\(.*$/, "").trim();

const flagsOf = (row: Placement): string[] =>
  (row.review_flag ?? "").split("|").map((f) => f.trim()).filter(Boolean);

/**
 * The review flag is one field holding three unrelated things, which is why the raw
 * "82% of rows flagged" reads as though the agent is unsure of almost everything. Only
 * IAB_AMBIGUOUS is the agent hedging; the rest are decisions a human makes anyway.
 */
const FLAG_KIND: Record<string, "unsure" | "human" | "nocat"> = {
  IAB_AMBIGUOUS: "unsure",
  UNCERTAIN_TYPE: "unsure",
  UNCERTAIN_SUITABILITY: "unsure",
  CAT_SENSITIVE: "human",
  IP_PLACEMENT: "human",
  NCC: "nocat",
  NO_MATCH: "nocat",
};

const FLAG_LABEL: Record<string, string> = {
  IAB_AMBIGUOUS: "category was a close call",
  UNCERTAIN_TYPE: "unsure Placement vs Narrative",
  UNCERTAIN_SUITABILITY: "unsure suitability",
  CAT_SENSITIVE: "brand would want to approve this",
  IP_PLACEMENT: "third-party IP, needs clearing",
  NCC: "no commercial category exists",
  NO_MATCH: "no category matched",
};

const SUIT_STYLE: Record<string, string> = {
  Positive: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  Neutral: "bg-slate-100 text-slate-700 hover:bg-slate-100",
  "Category Sensitive": "bg-amber-100 text-amber-800 hover:bg-amber-100",
  Negative: "bg-red-100 text-red-800 hover:bg-red-100",
  NCC: "bg-slate-200 text-slate-600 hover:bg-slate-200",
};

/** Which kind of line the product sat on — the fact the price is derived from. */
function BlockBadge({ block }: { block: string | null }) {
  if (block === "DIALOGUE" || block === "CHARACTER" || block === "PARENTHETICAL") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700">
        <MessageSquare className="h-3 w-3" /> spoken
      </span>
    );
  }
  if (block === "ACTION_HEARD") {
    return <span className="text-xs font-medium text-violet-700">heard only</span>;
  }
  if (!block) return <span className="text-xs text-slate-400">not located</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600">
      <Eye className="h-3 w-3" /> seen
    </span>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </div>
        <div className="mt-1.5 text-2xl font-semibold tracking-tight">{value}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
      </CardContent>
    </Card>
  );
}

/** One script-level field. Renders a single string or a list as chips. */
function SummaryField({
  label,
  values,
  accent = false,
}: {
  label: string;
  values: string | string[] | undefined;
  accent?: boolean;
}) {
  const list = values === undefined ? [] : Array.isArray(values) ? values : [values];
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {list.length === 0 ? (
          // Says which field is missing rather than rendering an empty gap, because a
          // blank here means the agent did not emit it, not that the script has no tone.
          <span className="text-xs text-slate-400">not emitted by this run</span>
        ) : (
          list.map((v) => (
            <Badge
              key={v}
              variant={accent ? "default" : "secondary"}
              className="font-normal"
            >
              {v}
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}

interface Props {
  /** A live run to display. Omitted on /placements, which shows the saved snapshot. */
  data?: PlacementRun;
  /** When that live run finished. Two runs of one script look nearly identical, so the
   *  clock is the only thing on screen that distinguishes them. */
  finishedAt?: Date;
}

export default function PlacementInventoryPage({ data, finishedAt }: Props = {}) {
  const live = data !== undefined;
  const { placements, run, summary, script } = data ?? snapshot;
  const [suitFilter, setSuitFilter] = useState<string>("all");
  const [labelFilter, setLabelFilter] = useState<string>("all");

  const stats = useMemo(() => {
    const ni = placements.filter((p) => p.integration_type === "Narrative Integration");
    const pp = placements.filter((p) => p.integration_type === "Product Placement");
    const ncc = placements.filter((p) => p.integration_type === "NCC");
    const density = placements.reduce((s, p) => s + (p.integration_density ?? 0), 0);
    const overridden = placements.filter(
      (p) => p.integration_type !== p.integration_type_llm,
    );

    // Split the flags by what they actually mean, not by whether one is present.
    const kinds = { unsure: new Set<string>(), human: new Set<string>(), nocat: new Set<string>() };
    placements.forEach((p, i) => {
      flagsOf(p).forEach((f) => {
        const kind = FLAG_KIND[f];
        if (kind) kinds[kind].add(String(i));
      });
    });

    // NI share by where the product sat. This is the number that used to point
    // backwards: 32% on spoken lines against 42% on seen ones, before the layout fix.
    const spoken = placements.filter((p) =>
      ["DIALOGUE", "CHARACTER", "PARENTHETICAL"].includes(p.script_block ?? ""),
    );
    const seen = placements.filter((p) => p.script_block === "ACTION");
    const share = (rows: Placement[]) =>
      rows.length
        ? Math.round(
            (100 * rows.filter((r) => r.integration_type === "Narrative Integration").length) /
              rows.length,
          )
        : 0;

    return {
      ni: ni.length,
      pp: pp.length,
      ncc: ncc.length,
      density,
      overridden: overridden.length,
      unsure: kinds.unsure.size,
      human: kinds.human.size,
      nocat: kinds.nocat.size,
      clean: placements.filter((p) => flagsOf(p).length === 0).length,
      spokenShare: share(spoken),
      seenShare: share(seen),
      spokenCount: spoken.length,
      seenCount: seen.length,
      multiCondition: placements.filter((p) => (p.narrative_function ?? "").includes("|")).length,
    };
  }, [placements]);

  const visible = useMemo(
    () =>
      placements.filter(
        (p) =>
          (suitFilter === "all" || p.segment_suitability === suitFilter) &&
          (labelFilter === "all" || p.integration_type === labelFilter),
      ),
    [placements, suitFilter, labelFilter],
  );

  const suitabilities = [...new Set(placements.map((p) => p.segment_suitability))].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* ---------- which run is this ---------- */}
        <div
          className={`rounded-md border px-4 py-2.5 text-sm ${
            live
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-slate-300 bg-white text-slate-700"
          }`}
        >
          {live ? (
            <>
              <strong>Live run</strong> — {script}
              {finishedAt && <> , finished {finishedAt.toLocaleTimeString()}</>} ·{" "}
              {run.wall_seconds.toFixed(0)}s · ${run.cost_usd.toFixed(2)} · {run.scenes} scenes
            </>
          ) : (
            <>
              <strong>Saved run</strong> — a recorded Detours run checked into the repo.
              Nothing is running here. To run the agent for real, go to{" "}
              <Link to="/placements/live" className="underline font-medium">
                /placements/live
              </Link>
              .
            </>
          )}
        </div>

        {/* ---------- header ---------- */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Placement agent · scene-level inventory
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">
            {script}{" "}
            <span className="text-slate-400 font-normal">episode {placements[0]?.scene_id && "101"}</span>
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Read from the script alone — nobody watched an episode and no audience data was
            used. Every row points back to a scene, a page, and the line it came from.
          </p>
        </div>


        {/* ---------- summary strip ---------- */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={FileText} label="Scenes" value={String(run.scenes)} />
          <StatCard icon={CheckCircle2} label="Products" value={String(placements.length)} />
          <StatCard
            icon={MessageSquare}
            label="Narrative"
            value={String(stats.ni)}
            hint="15s each"
          />
          <StatCard icon={Eye} label="Placement" value={String(stats.pp)} hint="6s each" />
          <StatCard icon={Clock} label="Runtime" value={`${(run.wall_seconds / 60).toFixed(1)}m`} />
          <StatCard icon={DollarSign} label="Cost" value={`$${run.cost_usd.toFixed(2)}`} />
        </div>

        {/* ---------- what this script IS ----------
            The other half of the inventory. A brand does not buy "73 products", it buys a
            show it wants to sit next to, and these are the fields a brand brief gets
            matched against. The agent has always emitted them; the page used to read
            content_rating and drop the rest. */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What this script is</CardTitle>
            <CardDescription>
              Read off the script itself, once, for the whole episode. This is what a brand
              brief gets matched against — the demand side of the same inventory.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <SummaryField label="Content rating" values={summary.content_rating} accent />
            <SummaryField label="Genre" values={summary.genre_focus} />
            <SummaryField label="Tone" values={summary.tonal_qualities} />
            <SummaryField label="Character archetypes" values={summary.character_archetypes} />
            <SummaryField label="Setting" values={summary.setting} />
            <SummaryField label="Cultural markers" values={summary.cultural_markers} />
          </CardContent>
        </Card>

        {/* ---------- health line ---------- */}
        <Card className="border-slate-200">
          <CardContent className="py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {run.schema_violations.length} schema violations
            </span>
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {run.layout_warnings.length === 0
                ? "script layout intact"
                : `${run.layout_warnings.length} layout warnings`}
            </span>
            <span className="text-slate-500 font-mono text-xs">
              {run.config.model} · seed {run.config.seed} · {run.config.votes} readings/scene ·
              prompt {run.config.prompt_version}
            </span>
          </CardContent>
        </Card>

        <Tabs defaultValue="inventory">
          <TabsList>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
            <TabsTrigger value="review">Where it is unsure</TabsTrigger>
          </TabsList>

          {/* ================= INVENTORY ================= */}
          <TabsContent value="inventory" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  Density {stats.density.toLocaleString()}
                </CardTitle>
                <CardDescription>
                  Density is <strong>seconds × mentions</strong>, and seconds comes only from
                  the Placement/Narrative label — 6 or 15. So the label and the price are the
                  same decision. Density is what becomes impressions and then PSV.
                  <br />
                  <span className="text-amber-700">
                    The 6 and 15 are a stand-in nobody has timed yet, so read density as a
                    ranking of what is worth more, not as seconds of screen time.
                  </span>
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className="text-slate-500">Filter:</span>
              {["all", "Narrative Integration", "Product Placement", "NCC"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLabelFilter(l)}
                  className={`px-2.5 py-1 rounded-md border text-xs transition ${
                    labelFilter === l
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {l === "all" ? "all labels" : l}
                </button>
              ))}
              <Separator orientation="vertical" className="h-5 mx-1" />
              {["all", ...suitabilities].map((s) => (
                <button
                  key={s}
                  onClick={() => setSuitFilter(s)}
                  className={`px-2.5 py-1 rounded-md border text-xs transition ${
                    suitFilter === s
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {s === "all" ? "all suitability" : s}
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-500">
                {visible.length} of {placements.length}
              </span>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-24">Scene</TableHead>
                      <TableHead>IAB category</TableHead>
                      <TableHead className="w-40">Label</TableHead>
                      <TableHead className="w-36">Suitability</TableHead>
                      <TableHead className="w-20 text-right">Density</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="align-top">
                          <div className="font-medium">{productName(p.raw_term)}</div>
                          <div className="text-xs text-slate-500 italic mt-0.5 max-w-md">
                            “{p.evidence_span}”
                          </div>
                        </TableCell>
                        <TableCell className="align-top font-mono text-xs text-slate-500">
                          {p.scene_id}
                          <br />p{p.page_number}
                        </TableCell>
                        <TableCell className="align-top text-sm">
                          {shortCategory(p.iab_category)}
                          {flagsOf(p).includes("IAB_AMBIGUOUS") && (
                            <span className="ml-2 text-[10px] uppercase font-semibold text-amber-700">
                              close call
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge
                            variant="outline"
                            className={
                              p.integration_type === "Narrative Integration"
                                ? "border-blue-300 text-blue-800"
                                : p.integration_type === "NCC"
                                  ? "border-slate-300 text-slate-500"
                                  : "border-slate-300 text-slate-700"
                            }
                          >
                            {p.integration_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge className={SUIT_STYLE[p.segment_suitability ?? ""] ?? ""}>
                            {p.segment_suitability}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top text-right font-mono text-sm">
                          {p.integration_density}
                          {(p.mentions ?? 1) > 1 && (
                            <div className="text-[10px] text-slate-400">
                              ×{p.mentions} mentions
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ================= REVIEW ================= */}
          <TabsContent value="review" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {stats.unsure} rows where the agent is genuinely unsure
                </CardTitle>
                <CardDescription>
                  {placements.length - stats.clean} of {placements.length} rows carry some flag,
                  which sounds alarming and is not one thing. The field mixes three unrelated
                  situations, so they are separated here. Only the first is the agent hedging.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
                    <AlertTriangle className="h-4 w-4" /> Agent unsure
                  </div>
                  <div className="mt-1 text-3xl font-semibold text-amber-900">
                    {stats.unsure}
                  </div>
                  <div className="text-xs text-amber-800/80">
                    the runner-up category was a hair away — a real accuracy risk, and the
                    number we are working to bring down
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    <ShieldAlert className="h-4 w-4" /> Needs a human decision
                  </div>
                  <div className="mt-1 text-3xl font-semibold text-slate-800">{stats.human}</div>
                  <div className="text-xs text-slate-500">
                    not errors — brand approval or IP clearance, which a person signs off
                    regardless of what the agent thinks
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-sm font-medium text-slate-800">No category exists</div>
                  <div className="mt-1 text-3xl font-semibold text-slate-800">{stats.nocat}</div>
                  <div className="text-xs text-slate-500">
                    nothing in the taxonomy fits — priced at 0
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category chosen</TableHead>
                      <TableHead className="w-72">Why it is flagged</TableHead>
                      <TableHead className="w-20">Readings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {placements
                      .filter((p) => flagsOf(p).length > 0)
                      .map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="align-top">
                            <div className="text-sm font-medium">{productName(p.raw_term)}</div>
                            <div className="font-mono text-[10px] text-slate-400">
                              {p.scene_id}
                            </div>
                          </TableCell>
                          <TableCell className="align-top text-sm">
                            {shortCategory(p.iab_category)}
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="space-y-0.5">
                              {flagsOf(p).map((f) => (
                                <div
                                  key={f}
                                  className={`text-xs ${
                                    FLAG_KIND[f] === "unsure"
                                      ? "text-amber-800"
                                      : "text-slate-600"
                                  }`}
                                >
                                  {FLAG_LABEL[f] ?? f}
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="align-top font-mono text-xs text-slate-500">
                            {p.votes}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ---------- honest limits ---------- */}
        <Card className="border-slate-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What this is not yet</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-1.5 pb-5">
            <p>
              <strong>No dollar figure.</strong> This is inventory, not value. Price is
              inventory × audience, and the audience forecast is a separate model still being
              built.
            </p>
            <p>
              <strong>The 6 and 15 seconds are a proxy.</strong> Nothing has been timed. They
              rank what is worth more; they are not a measurement.
            </p>
            <p>
              <strong>{stats.unsure} categories are close calls.</strong> The agent flags its
              own near-ties rather than guessing quietly, so treat this as a review queue.
            </p>
            <p>
              <strong>Figurative language still gets priced.</strong> A simile or a brand used
              as a verb can come through as inventory; on a hand-checked sample that was about
              17% of rows.
            </p>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 pb-4">
          Snapshot of a real agent run over {run.scenes} scenes ·{" "}
          {run.config.votes} readings per scene, keeping products {run.config.keep} agree on ·{" "}
          {run.config.iab_selection} category selection · static data, no backend required.
        </p>
      </div>
    </div>
  );
}
